// What one table cell in a shared snapshot shows.
//
// A select cell stores the id of one of its column's choices ("o4"), never the
// choice's name. The viewer printed the id, so every shared table read "o1",
// "o4", "o11" where the sender saw "Stocks" and "Watching". Snapshots now carry
// each select column's choices (the desktop's shareSnapshot.serializeColumn),
// and a cell shows the choice's name.
//
// A snapshot made before that has no names to show. Printing the id would be
// the same meaningless "o4"; guessing a name would be inventing the sender's
// data. So the cell shows "—", as the app itself shows a value whose choice it
// cannot find, and says so (`unnamed`), so the table can tell the reader why.
//
// Pure and DOM-free, so the Node tests load it as written.

export interface CellOption {
  id: string
  label: string
  color?: string
}

export interface CellColumn {
  id: string
  type: string
  label: string
  /** single-select and multi-select: the choices a cell's value names. */
  options?: CellOption[]
}

export interface CellShown {
  text: string
  /** A choice was set, but the snapshot does not say what it is called. */
  unnamed: boolean
}

const EMPTY = '—'

function optionLabel(column: CellColumn, id: unknown): string | null {
  if (typeof id !== 'string') return null
  const found = (Array.isArray(column.options) ? column.options : []).find((o) => o && o.id === id)
  return found && typeof found.label === 'string' ? found.label : null
}

export function cellText(value: unknown, column: CellColumn): CellShown {
  if (value == null || value === '') return { text: EMPTY, unnamed: false }
  if (column.type === 'single-select') {
    const label = optionLabel(column, value)
    return label !== null ? { text: label, unnamed: false } : { text: EMPTY, unnamed: true }
  }
  if (column.type === 'multi-select') {
    const ids = Array.isArray(value) ? value : [value]
    if (ids.length === 0) return { text: EMPTY, unnamed: false }
    const labels = ids.map((id) => optionLabel(column, id))
    const named = labels.filter((l): l is string => l !== null)
    return { text: named.length ? named.join(', ') : EMPTY, unnamed: named.length < ids.length }
  }
  if (column.type === 'date' && typeof value === 'number') {
    return { text: new Date(value).toLocaleDateString(), unnamed: false }
  }
  if (column.type === 'checkbox') return { text: value ? '✓' : '', unnamed: false }
  if (typeof value === 'object') return { text: JSON.stringify(value), unnamed: false }
  return { text: String(value), unnamed: false }
}

/** The columns of a table whose shown rows have a choice the snapshot does not name. */
export function unnamedColumns(
  columns: CellColumn[],
  rows: Array<{ cells: Record<string, unknown> }>
): string[] {
  return columns
    .filter((c) => rows.some((r) => cellText(r.cells?.[c.id], c).unnamed))
    .map((c) => c.label || c.id)
}
